import { ref, get } from 'firebase/database';
import { database } from '../firebase';
import { buildCacheKey, getCachedData, getCachedDataWithVersion, setCachedData, removeCachedData } from '../utils/cache';

const CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 días
const LOCAL_CACHE_VERSION = '1.0.0';
const LOCAL_STORAGE_KEYS = {
  VERSION: 'vortex_cache_exani_data_version',
  MODULOS: 'exani_modulos',
  CARRERAS: 'exani_carreras',
  PREGUNTAS_PREFIX: 'exani_preguntas_',
  PRELOAD_COMPLETE: 'exani_preload_complete',
};

class ExaniModulosService {
  constructor() {
    this.db = database;
    this.cache = new Map();
  }

  getFromCache(key, ttlMs = CACHE_TTL) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < ttlMs) {
      return cached.data;
    }
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Obtiene el número de versión de los datos de módulos desde Firebase.
   * Permite invalidar el cache cuando hay cambios.
   */
  async getDataVersion() {
    try {
      const versionRef = ref(this.db, 'config/exani_modulos_version');
      const snapshot = await get(versionRef);
      if (snapshot.exists()) {
        return snapshot.val();
      }
    } catch (error) {
      console.warn('No se pudo obtener la versión de datos de módulos:', error);
    }
    return null;
  }

  /**
   * Verifica si el cache local sigue vigente según la versión de Firebase.
   * Si la versión en Firebase difiere de la versión local, invalida todo el cache.
   */
  async validateCache() {
    const firebaseVersion = await this.getDataVersion();
    if (!firebaseVersion) {
      const localVersion = localStorage.getItem(LOCAL_STORAGE_KEYS.VERSION);
      if (!localVersion) {
        return false;
      }
      return true;
    }

    const localVersion = localStorage.getItem(LOCAL_STORAGE_KEYS.VERSION);
    if (localVersion !== String(firebaseVersion)) {
      this.clearAllCache();
      localStorage.setItem(LOCAL_STORAGE_KEYS.VERSION, String(firebaseVersion));
      return false;
    }

    // Verificar que los datos existan
    const modulosCached = getCachedData(LOCAL_STORAGE_KEYS.MODULOS);
    if (!modulosCached) {
      return false;
    }

    return true;
  }

  /**
   * Limpia todo el cache local relacionado con módulos EXANI
   */
  clearAllCache() {
    this.cache.clear();
    removeCachedData(LOCAL_STORAGE_KEYS.MODULOS);
    removeCachedData(LOCAL_STORAGE_KEYS.CARRERAS);
    removeCachedData(LOCAL_STORAGE_KEYS.PRELOAD_COMPLETE);

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(buildCacheKey(LOCAL_STORAGE_KEYS.PREGUNTAS_PREFIX))) {
        removeCachedData(key.replace(buildCacheKey(''), ''));
      }
    });
  }

  /**
   * Descarga todos los módulos y carreras desde Firebase y los cachea localmente.
   * Este método se llama en background en el primer acceso.
   */
  async preloadData(onProgress = null) {
    try {
      const [modulosData, carrerasData] = await Promise.all([
        this.getModulos(true),
        this.getCarreras(true),
      ]);

      onProgress?.('modules', modulosData.length);

      localStorage.setItem(LOCAL_STORAGE_KEYS.PRELOAD_COMPLETE, 'true');
      localStorage.setItem(LOCAL_STORAGE_KEYS.VERSION, LOCAL_CACHE_VERSION);

      return { modulos: modulosData, carreras: carrerasData };
    } catch (error) {
      console.error('Error durante el pre-cargado de datos:', error);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.PRELOAD_COMPLETE);
      throw error;
    }
  }

  /**
   * Precarga las preguntas de todos los módulos después de obtenerlos.
   * Se ejecuta en background sin bloquear.
   */
  async preloadPreguntasModulos(modulos, onProgress = null) {
    if (!modulos || modulos.length === 0) return;

    for (const modulo of modulos) {
      try {
        await this.getPreguntasModulo(modulo.moduloKey, true);
        onProgress?.(modulo.moduloKey);
      } catch (error) {
        console.warn(`No se pudieron cachear preguntas para ${modulo.moduloKey}:`, error);
      }
    }
  }

  /**
   * Verifica si el pre-cargado ha sido completado previamente.
   */
  isPreloadComplete() {
    return localStorage.getItem(LOCAL_STORAGE_KEYS.PRELOAD_COMPLETE) === 'true';
  }

  /**
   * Obtiene la lista de los 22 módulos EXANI-II desde RTDB (/modulos_metadata o fallback /modulos)
   * @param {boolean} force - Si es true, ignora el cache y descarga de Firebase
   */
  async getModulos(force = false) {
    const cacheKey = LOCAL_STORAGE_KEYS.MODULOS;

    if (!force) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;

      const localCached = getCachedData(cacheKey);
      if (localCached) {
        this.setCache(cacheKey, localCached);
        return localCached;
      }
    }

    try {
      const metadataRef = ref(this.db, 'modulos_metadata');
      let snapshot = await get(metadataRef);
      let isDecoupled = true;

      if (!snapshot.exists()) {
        const modulosRef = ref(this.db, 'modulos');
        snapshot = await get(modulosRef);
        isDecoupled = false;
      }

      if (!snapshot.exists()) return [];

      const val = snapshot.val();
      const list = Object.keys(val).map(key => {
        const item = val[key];
        const preguntasObj = (!isDecoupled && item.preguntas) ? item.preguntas : {};
        const totalCalculado = Object.keys(preguntasObj).length;
        const total = item.total !== undefined ? item.total : totalCalculado;
        const objetivo = item.objetivo || 250;

        return {
          moduloKey: item.modulo_key || key,
          nombre: item.nombre || key,
          area: item.area || '',
          total: total,
          objetivo: objetivo,
          faltantes: item.faltantes !== undefined ? item.faltantes : Math.max(0, objetivo - total)
        };
      }).sort((a, b) => a.nombre.localeCompare(b.nombre));

      this.setCache(cacheKey, list);
      setCachedData(cacheKey, list, LOCAL_CACHE_VERSION);
      return list;
    } catch (error) {
      console.error('Error al obtener módulos EXANI-II:', error);
      const cached = getCachedData(cacheKey);
      if (cached) {
        console.warn('Usando cache local por error en Firebase:', error);
        return cached;
      }
      throw error;
    }
  }

  /**
   * Obtiene la lista de carreras EXANI-II desde RTDB (/carreras)
   * @param {boolean} force - Si es true, ignora el cache y descarga de Firebase
   */
  async getCarreras(force = false) {
    const cacheKey = LOCAL_STORAGE_KEYS.CARRERAS;

    if (!force) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;

      const localCached = getCachedData(cacheKey);
      if (localCached) {
        this.setCache(cacheKey, localCached);
        return localCached;
      }
    }

    try {
      const carrerasRef = ref(this.db, 'carreras');
      const snapshot = await get(carrerasRef);

      if (!snapshot.exists()) return [];

      const val = snapshot.val();
      const list = Object.keys(val).map(key => ({
        slug: key,
        ...val[key]
      })).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));

      this.setCache(cacheKey, list);
      setCachedData(cacheKey, list, LOCAL_CACHE_VERSION);
      return list;
    } catch (error) {
      console.error('Error al obtener carreras EXANI-II:', error);
      const cached = getCachedData(cacheKey);
      if (cached) {
        console.warn('Usando cache local por error en Firebase:', error);
        return cached;
      }
      throw error;
    }
  }

  /**
   * Obtiene los reactivos/preguntas de un módulo específico desde RTDB
   * (/modulos/{moduloKey}/preguntas)
   * @param {string} moduloKey - La clave del módulo
   * @param {boolean} force - Si es true, ignora el cache y descarga de Firebase
   */
  async getPreguntasModulo(moduloKey, force = false) {
    const cacheKey = `${LOCAL_STORAGE_KEYS.PREGUNTAS_PREFIX}${moduloKey}`;

    if (!force) {
      const cached = this.getFromCache(cacheKey);
      if (cached) return cached;

      const localCached = getCachedData(cacheKey);
      if (localCached) {
        this.setCache(cacheKey, localCached);
        return localCached;
      }
    }

    try {
      const preguntasRef = ref(this.db, `modulos/${moduloKey}/preguntas`);
      const snapshot = await get(preguntasRef);

      if (!snapshot.exists()) return [];

      const val = snapshot.val();
      const list = Object.keys(val).map(id => {
        const p = val[id];
        let opcionesList = [];
        if (Array.isArray(p.opciones)) {
          opcionesList = p.opciones;
        } else if (p.opciones && typeof p.opciones === 'object') {
          opcionesList = Object.values(p.opciones);
        } else if (p.respuestas && typeof p.respuestas === 'object') {
          opcionesList = Object.values(p.respuestas);
        }

        return {
          id: p.id || id,
          pregunta: p.pregunta || '',
          opciones: opcionesList,
          correcta: p.correcta !== undefined ? p.correcta : '',
          feedback: p.feedback || p.explicacion || '',
          area: p.area || moduloKey,
          grafica: p.grafica || null
        };
      });

      this.setCache(cacheKey, list);
      setCachedData(cacheKey, list, LOCAL_CACHE_VERSION);
      return list;
    } catch (error) {
      console.error(`Error al obtener preguntas del módulo ${moduloKey}:`, error);
      const cached = getCachedData(cacheKey);
      if (cached) {
        console.warn('Usando cache local por error en Firebase:', error);
        return cached;
      }
      throw error;
    }
  }
}

export const exaniModulosService = new ExaniModulosService();
export default exaniModulosService;
